import type {IconName} from '@wandb/components';

/**
 * AgentSuggestion is one new-chat suggestion chip. `prompt` is the text sent on
 * click and defaults to `title`. `sentLabel` is what the sent bubble reads once
 * clicked, for chips whose title works as a button but not as something the
 * user would say; it defaults to `title`.
 */
export interface AgentSuggestion {
  title: string;
  prompt?: string;
  sentLabel?: string;
  icon: IconName;
}

/**
 * Prompts submitted by ARIA entry points outside the chat drawer.
 */
export const entryPointPrompts = {
  filterRuns: (request: string) =>
    `Update this workspace's run filters based on this request: ${request}`,
} as const;

/** Contextual composer guidance shown by non-submitting ARIA entry points. */
export const entryPointPlaceholders = {
  runsColumns: "Describe the columns you'd like to show",
  createReport: "Describe the report you'd like to create",
  improveReport: "Describe how you'd like to improve this report",
  createSavedView: "Describe the saved view you'd like to create",
  run: 'Ask ARIA about this run',
  panel: 'Ask ARIA about this panel',
} as const;

export type WBAgentCallKind = 'trace' | 'evaluation' | 'evaluation-result';

export const callReferenceLabels: Record<WBAgentCallKind, string> = {
  trace: 'Trace',
  evaluation: 'Eval',
  'evaluation-result': 'Eval result',
};

export function callReferenceKind(displayName?: string): WBAgentCallKind {
  const label = displayName?.toLowerCase();
  if (label?.startsWith('eval result')) {
    return 'evaluation-result';
  }
  if (label?.startsWith('eval')) {
    return 'evaluation';
  }
  return 'trace';
}

export function callReferencePlaceholder(
  kind: WBAgentCallKind,
  count = 1
): string {
  const noun = callReferenceLabels[kind].toLowerCase();
  return `Ask ARIA about ${count === 1 ? `this ${noun}` : `these ${noun}s`}`;
}

// Suggestion chips share the accent tone; see AgentSuggestionList.

// Shared

export const summarizeReport: AgentSuggestion = {
  title: 'Summarize this project in a report',
  icon: 'report',
};

export const inferenceModels: AgentSuggestion = {
  title: 'What models can I use on Inference?',
  icon: 'magic-wand-star',
};

export const draftAReport: AgentSuggestion = {
  title: 'Help me draft a report',
  icon: 'report',
};

export const summarizeRecentWork: AgentSuggestion = {
  title: 'Summarize my recent work',
  icon: 'recent-clock',
};

export const patterns: AgentSuggestion = {
  title: 'What patterns do you see in my results?',
  icon: 'chart-line',
};

export const whatCanYouDo: AgentSuggestion = {
  title: 'What can you do?',
  prompt: '/intro',
  icon: 'help-alt',
};

export const roastMyProject: AgentSuggestion = {
  title: 'Roast my project',
  icon: 'explosion',
};

export const recommendNextExperiment: AgentSuggestion = {
  title: 'Recommend my next experiment',
  icon: 'lightbulb-info',
};

// ARIA landing — onboarding CTAs. Each sends a slash command that launches the
// matching onboarding skill under the hood.

export const onboardingFineTuning: AgentSuggestion = {
  title: 'Get started with post-training',
  prompt: '/onboarding-fine-tuning',
  sentLabel: 'Can you help me get started with post-training?',
  icon: 'settings-parameters',
};

export const onboardingAgentMonitoring: AgentSuggestion = {
  title: 'Get started with agent monitoring',
  prompt: '/onboarding-agent-monitoring',
  sentLabel: 'Can you help me get started with agent monitoring?',
  icon: 'automation-robot-arm',
};

export const onboardingModelTraining: AgentSuggestion = {
  title: 'Get started with model training',
  prompt: '/onboarding-model-training',
  sentLabel: 'Can you help me get started with model training?',
  icon: 'chart-line',
};

// Workspace

export const autoresearchQuickstart: AgentSuggestion = {
  title: 'Launch experiments with autoresearch',
  prompt: '/autoresearch-quickstart',
  icon: 'rocket-launch',
};

// Default

export const recentCrashedRuns: AgentSuggestion = {
  title: 'Have any of my recent runs crashed?',
  icon: 'warning',
};

export const starredReports: AgentSuggestion = {
  title: 'Find the reports I starred',
  icon: 'star',
};

export const recentlyFinishedRuns: AgentSuggestion = {
  title: 'Which runs finished most recently?',
  icon: 'recent-clock',
};

// Runs table

export const setBaseline: AgentSuggestion = {
  title: 'Set my best run as the baseline',
  icon: 'baseline-alt',
};

// Run logs

export const analyzeLogs: AgentSuggestion = {
  title: 'Analyze my logs',
  icon: 'list-bullets',
};

// Run (Overview)

export const compareRecentRuns: AgentSuggestion = {
  title: 'How does this run compare to recent runs?',
  icon: 'chart-scatterplot',
};

// Non-project / org / profile

export const crashedRuns: AgentSuggestion = {
  title: 'How many of my runs crashed this past week?',
  icon: 'warning',
};

export const activeRuns: AgentSuggestion = {
  title: 'Find all of my active runs',
  icon: 'running-repeat',
};

export const starProjects: AgentSuggestion = {
  title: 'Star the projects I visit the most',
  icon: 'star',
};

// Registry

export const collectionsActivity: AgentSuggestion = {
  title: 'Show me collections with recent activity',
  icon: 'recent-clock',
};

export const cleanupRegistries: AgentSuggestion = {
  title: 'Help me clean up my registries',
  icon: 'delete',
};

export const registriesWithAutomations: AgentSuggestion = {
  title: 'Which Registries have Automations?',
  icon: 'automation-robot-arm',
};

// Automations

export const understandAutomations: AgentSuggestion = {
  title: 'Help me understand these automations',
  icon: 'automation-robot-arm',
};

export const createAutomation: AgentSuggestion = {
  title: 'Help me create an automation',
  icon: 'add-new',
};

export const configureGithubAction: AgentSuggestion = {
  title: 'Help me configure a Github action',
  icon: 'github',
};

// Weave (conversations)

export const frustratedUsers: AgentSuggestion = {
  title: 'What did frustrated users say?',
  icon: 'thumbs-down',
};

export const summarizeConversations: AgentSuggestion = {
  title: 'Summarize the last conversation/traces',
  icon: 'forum-chat-bubble',
};

// Weave (signals)

export const createSignal: AgentSuggestion = {
  title: 'Create a new signal',
  icon: 'add-new',
};

export const signalsFound: AgentSuggestion = {
  title: 'What signals have you found?',
  icon: 'search',
};

// Agent Lens. One set for the whole product: a user here is instrumenting an
// agent, reading Insights, or setting up Tags (online LLM judges), whichever
// page they happen to be on.

export const instrumentMyAgent: AgentSuggestion = {
  title: 'How can I instrument my agent?',
  icon: 'automation-robot-arm',
};

export const getStartedWithInsights: AgentSuggestion = {
  title: 'How can I get started with Insights?',
  icon: 'lightbulb-info',
};

export const setUpOnlineJudges: AgentSuggestion = {
  title: 'How can I set up online LLM judges?',
  icon: 'number-hashtag',
};

// Weave (evaluations)

export const createDataset: AgentSuggestion = {
  title: 'Help me create a dataset',
  icon: 'table',
};

export const createScorers: AgentSuggestion = {
  title: 'Help me create scorers',
  icon: 'number-hashtag',
};

export const evalImproved: AgentSuggestion = {
  title: 'Did my latest eval improve and why?',
  icon: 'chart-line',
};

// Sweeps list

export const bestSweep: AgentSuggestion = {
  title: 'Which sweep found the best result?',
  icon: 'magic-wand-star',
};

export const runningSweeps: AgentSuggestion = {
  title: 'Which sweeps are still running?',
  icon: 'running-repeat',
};

export const sweepParameters: AgentSuggestion = {
  title: 'Which parameters mattered most?',
  icon: 'settings-parameters',
};

// Project jobs

export const describeJobs: AgentSuggestion = {
  title: 'What do these jobs do?',
  icon: 'info',
};

export const recentJobRuns: AgentSuggestion = {
  title: 'Which jobs ran most recently?',
  icon: 'recent-clock',
};

// Launch (queues, agents)

export const stuckQueues: AgentSuggestion = {
  title: 'Are any of my queues stuck?',
  icon: 'warning',
};

export const queueActivity: AgentSuggestion = {
  title: 'Summarize activity across my queues',
  icon: 'list-bullets',
};

// Project overview — Details

export const projectSizeAndActivity: AgentSuggestion = {
  title: "Summarize this project's size and activity",
  icon: 'chart-line',
};

export const projectVisibility: AgentSuggestion = {
  title: 'Who can see this project?',
  icon: 'info',
};

export const projectApiSnippet: AgentSuggestion = {
  title: 'Show me the API code to pull these runs',
  icon: 'list-bullets',
};

// Project overview — Weave tab

export const traceVolumeAndCost: AgentSuggestion = {
  title: 'Summarize my trace volume and cost',
  icon: 'chart-line',
};

export const tokenSpend: AgentSuggestion = {
  title: 'What is driving my token spend?',
  icon: 'number-hashtag',
};

export const traceErrors: AgentSuggestion = {
  title: 'Are traces erroring more than usual?',
  icon: 'warning',
};

// Project overview — Project Roles tab

export const projectAccess: AgentSuggestion = {
  title: 'Who has access to this project?',
  icon: 'info',
};

export const projectAdmins: AgentSuggestion = {
  title: 'Which members are admins?',
  icon: 'search',
};

export const explainRoles: AgentSuggestion = {
  title: 'Explain these project roles',
  icon: 'list-bullets',
};

// Project overview — AI Providers tab

export const configuredProviders: AgentSuggestion = {
  title: 'Which AI providers are configured?',
  icon: 'info',
};

export const addCustomRuntime: AgentSuggestion = {
  title: 'Help me add a custom runtime',
  icon: 'add-new',
};

export const whereProvidersApply: AgentSuggestion = {
  title: 'Where do these providers get used?',
  icon: 'search',
};

// Inference

export const trackInferenceUsage: AgentSuggestion = {
  title: 'Track my inference usage',
  icon: 'chart-line',
};

export const useLoraAdapter: AgentSuggestion = {
  title: 'Help me use my own LoRA adapter',
  icon: 'magic-wand-star',
};

export const compareModelsInPlayground: AgentSuggestion = {
  title: 'Help me compare models in the Playground',
  icon: 'chart-scatterplot',
};

// Post-Training. One set for the whole product: the landing page and the
// Model Distillation app share the `post-training` product id. The onboarding
// chip (`onboardingFineTuning`, above) leads the set.

export const startFineTuningJob: AgentSuggestion = {
  title: 'Help me start a fine-tuning job',
  icon: 'rocket-launch',
};

export const explainModelDistillation: AgentSuggestion = {
  title: 'How does Model Distillation work?',
  icon: 'magic-wand-star',
};

// Marimo notebooks

export const startANotebook: AgentSuggestion = {
  title: 'Help me start a notebook',
  icon: 'add-new',
};

export const whatNotebooksDo: AgentSuggestion = {
  title: 'What are these notebooks doing?',
  icon: 'info',
};

export const cleanUpNotebooks: AgentSuggestion = {
  title: 'Help me clean up old notebooks',
  icon: 'delete',
};

// Reports list

export const recentReports: AgentSuggestion = {
  title: 'Which reports changed recently?',
  icon: 'recent-clock',
};

export const whatReportsCover: AgentSuggestion = {
  title: 'What do these reports cover?',
  icon: 'info',
};

// A single report

export const summarizeThisReport: AgentSuggestion = {
  title: 'Summarize this report',
  icon: 'report',
};

export const improveThisReport: AgentSuggestion = {
  title: 'Improve this report',
  icon: 'magic-wand-star',
};

export const checkReportNumbers: AgentSuggestion = {
  title: 'Check these numbers against the runs',
  icon: 'search',
};

// Artifacts

export const whatArtifactsExist: AgentSuggestion = {
  title: 'What artifacts does this project hold?',
  icon: 'table',
};

export const cleanUpArtifacts: AgentSuggestion = {
  title: 'Help me clean up old versions',
  icon: 'delete',
};

export const artifactLineage: AgentSuggestion = {
  title: 'Where did this artifact come from?',
  icon: 'search',
};

// Weave Playground

export const comparePromptModels: AgentSuggestion = {
  title: 'Compare two models on this prompt',
  icon: 'chart-scatterplot',
};

export const improveThisPrompt: AgentSuggestion = {
  title: 'Help me write a better prompt',
  icon: 'magic-wand-star',
};

export const promptToEval: AgentSuggestion = {
  title: 'Turn this into an evaluation',
  icon: 'number-hashtag',
};

// A single sweep

export const bestRunsInSweep: AgentSuggestion = {
  title: 'Which runs in this sweep did best?',
  icon: 'magic-wand-star',
};

export const keepSweepRunning: AgentSuggestion = {
  title: 'Should I keep this sweep running?',
  icon: 'lightbulb-info',
};

// Profile — Stars tab

export const whatIStarred: AgentSuggestion = {
  title: 'Remind me what I starred',
  icon: 'search',
};

export const activeStarredProjects: AgentSuggestion = {
  title: 'Which of these are still active?',
  icon: 'running-repeat',
};

// Weave threads

export const slowestThreads: AgentSuggestion = {
  title: 'Which threads are slowest?',
  icon: 'chart-line',
};

export const longestThreads: AgentSuggestion = {
  title: 'Which threads took the most turns?',
  icon: 'number-hashtag',
};

export const threadTopics: AgentSuggestion = {
  title: 'What are people asking about?',
  icon: 'forum-chat-bubble',
};

// Weave leaderboards

export const leaderboardWinner: AgentSuggestion = {
  title: 'Which model is ahead, and on what?',
  icon: 'magic-wand-star',
};

export const leaderboardGaps: AgentSuggestion = {
  title: 'Which models are missing results?',
  icon: 'search',
};

export const buildLeaderboard: AgentSuggestion = {
  title: 'Help me build a leaderboard',
  icon: 'add-new',
};

// Weave assets — shared across the class lists

export const assetInventory: AgentSuggestion = {
  title: 'What is published here?',
  icon: 'table',
};

export const assetRecentChanges: AgentSuggestion = {
  title: 'What changed most recently?',
  icon: 'recent-clock',
};

export const assetUsage: AgentSuggestion = {
  title: 'Which of these are actually used?',
  icon: 'search',
};

// Weave assets — per class

export const modelVersionHistory: AgentSuggestion = {
  title: 'How has this model changed?',
  icon: 'chart-line',
};

export const datasetContents: AgentSuggestion = {
  title: 'What is in these datasets?',
  icon: 'table',
};

export const promptVariables: AgentSuggestion = {
  title: 'What variables do these prompts take?',
  icon: 'number-hashtag',
};

export const scorerBehavior: AgentSuggestion = {
  title: 'What do these scorers measure?',
  icon: 'number-hashtag',
};

export const evalDefinitionContents: AgentSuggestion = {
  title: 'What does each evaluation run?',
  icon: 'info',
};

export const opInventory: AgentSuggestion = {
  title: 'What does this project instrument?',
  icon: 'table',
};

// One Launch queue

export const queueNotRunning: AgentSuggestion = {
  title: "Why isn't this queue running?",
  icon: 'warning',
};

export const queueFailures: AgentSuggestion = {
  title: 'Which runs from this queue failed?',
  icon: 'search',
};
